import * as Sentry from "@sentry/react-native";
import type { Reporter, Tags } from "../types";
import { replayIntegration } from "./replay-integration";

export interface SentryReporterOptions {
  readonly dsn: string;
  /** Matches the release source maps are uploaded under, e.g. `a3s-app@2026.10.3`. */
  readonly release: string;
  readonly environment: string;
  /** The share of sessions traced for performance, from 0 to 1. */
  readonly tracesSampleRate: number;
  /**
   * URLs that get `sentry-trace` and `baggage` headers on outgoing requests.
   * Omitted, the SDK's default applies: same-origin on web, every URL on native.
   */
  readonly tracePropagationTargets?: readonly (string | RegExp)[];
  /** Set on every event, e.g. the native build profile. */
  readonly tags?: Tags;
}

/**
 * Sends errors to Sentry through the SDK's global client. The app must resolve
 * the same copy of `@sentry/react-native` as this package, so the root
 * component wrapped with `Sentry.wrap` and this reporter share a client (see
 * `apps/a3s-app/metro.config.js`).
 */
export function sentryReporter(options: SentryReporterOptions): Reporter {
  return {
    init: () => {
      Sentry.init({
        dsn: options.dsn,
        release: options.release,
        environment: options.environment,
        // Users are identified by id only (`identify`); no IP address, cookies or request bodies.
        sendDefaultPii: false,
        tracesSampleRate: options.tracesSampleRate,
        ...(options.tracePropagationTargets !== undefined && {
          tracePropagationTargets: [...options.tracePropagationTargets],
        }),
        // A replay costs quota and may show camera footage or contact details, so only
        // sessions that hit an error keep one, and the SDK masks content by default.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1,
        integrations: [replayIntegration()],
      });
      if (options.tags !== undefined) {
        Sentry.setTags(options.tags);
      }
    },
    captureException: ({ error, tags, extra, groupBy }) => {
      Sentry.captureException(error, {
        tags,
        extra,
        // `{{ default }}` keeps Sentry's own grouping and splits it further by these values.
        ...(groupBy.length > 0 && { fingerprint: ["{{ default }}", ...groupBy] }),
      });
    },
    addBreadcrumb: (breadcrumb) => {
      Sentry.addBreadcrumb(breadcrumb);
    },
    setUser: (user) => {
      Sentry.setUser(user === null ? null : { id: user.userId });
    },
    setTags: (tags) => {
      // An undefined tag stays on the scope but is dropped when an event is serialised.
      Sentry.setTags(Object.fromEntries(Object.entries(tags).map(([key, value]) => [key, value ?? undefined])));
    },
  };
}
